// Gestión avanzada del bot (trailing stop, breakeven, SL dinámico) — estado
// plano del form + traducción al contrato de backend/app/schemas/bot.py
// (TrailingConfig / BreakevenConfig / DynamicSLConfig). Aislado en su propio
// módulo para testearlo con vitest sin montar componentes, mismo patrón que
// buildBotPayload.js. Los campos y defaults se rescatan de la app privada
// (BotEditPage.jsx flattenBot/buildPayload).
//
// Doctrina de flags explícitos (fix 422): desactivado => { enabled: false }
// EXPLÍCITO, nunca {} — el trailing_worker los activaría con defaults
// implícitos si llegasen vacíos (ver cabecera de buildBotPayload.js).

// Unidades (WP-1, D2-R, Marc 2026-10-12): las activaciones (trailing, BE
// por umbral) y el paso del SL dinámico son MÚLTIPLOS DE R -- R = distancia
// entrada→SL inicial. Es como el engine ya los aplicaba; antes la UI los
// rotulaba "% de beneficio" (falso). El callback del trailing y el lock del
// BE siguen siendo % del precio. Sin SL no hay R: esas opciones se bloquean.
//
// Breakeven con dos disparadores independientes (WP-1): por umbral en R
// (breakevenEnabled + breakevenActivation) y al tocar TP1
// (breakevenOnTp1). Default de creación = lo que el engine hacía siempre
// sin preguntar (D1-A): ON a 1R y al tocar TP1, lock 0.

// Claves planas del form (compartidas por wizard paso 2 y EditBotPanel).
// Delta WP-1 (doctrina Marc 2026-10-13): el breakeven nunca cierra en
// pérdidas. Textos únicos para el wizard/panel y el form privado.
export const BREAKEVEN_LOCK_LABEL = 'Beneficio extra (%)'
export const BREAKEVEN_LOCK_HINT =
  'El breakeven nunca cierra en pérdidas: el SL se coloca más allá de la entrada lo justo para cubrir ' +
  'las comisiones de apertura y cierre, el funding ya pagado y un margen de deslizamiento (≈0,27 % en Bitunix). ' +
  'Este valor añade beneficio extra sobre eso; 0 = solo cubrir costes. Aplica a los dos disparadores. ' +
  'No incluye el funding que se cobre después de activarse, y un hueco de precio mayor que el margen puede dejar el cierre ligeramente en negativo.'

// Textos del WP «Gestión del trade completa en la web»
// (docs/bots/diseno-gestion-trade-web-2026-10-27.md §6.1) — literales T1-T5.
// T1 (selector de tipo de trailing).
export const TRAILING_TYPE_HINT =
  'El trailing sigue al precio una vez activado. Elige cómo se aleja el stop: un % fijo desde el máximo, ' +
  'o k veces el ATR (volatilidad) desde el pico.'
// T2 (trailing por ATR).
export const TRAILING_ATR_HINT =
  'Chandelier: el stop queda a k × ATR por debajo del pico alcanzado (por encima en corto). ' +
  'Solo sube: si la volatilidad se amplía, el stop no retrocede. ' +
  'Nunca queda por debajo del breakeven si este ya está activo.'
// T3 (SL al TP conquistado — solo Opc. 1, niveles del indicador).
export const CONQUERED_TP_HINT =
  'Al alcanzar el TP2, el stop sube al precio del TP1; al alcanzar el TP3, al del TP2, y así. ' +
  'El TP1 ya mueve el stop a breakeven.'
// T4 (convivencia stop dinámico + trailing — informativo, no error).
export const DYNAMIC_TRAILING_COEXIST_HINT =
  'El stop dinámico y el trailing pueden estar activos a la vez: se aplica el stop más favorable en cada momento.'
// T5 (caso especial de 1 solo TP con cierre parcial — aviso, §3.1/§4.2.3).
export const CONQUERED_SINGLE_TP_WARNING =
  'Con un solo TP de cierre parcial, al tocarlo el stop sube al precio del propio TP — más agresivo ' +
  'que con varios TPs — y el resto de la posición corre sin más gestión de este tipo.'

// Mensajes espejo EXACTOS de los 422 del backend (§4.2 del diseño).
export const TRAILING_SLOT_ERROR = 'El trailing solo admite un tipo: elige retroceso (%) o ATR, no ambos.'
export const TRAILING_ATR_PARAMS_ERROR = 'Trailing por ATR: indica el periodo del ATR (≥ 2) y el multiplicador (> 0).'
export const CONQUERED_TP_SOURCE_ERROR = 'El SL al TP conquistado requiere usar los TP/SL nativos del indicador.'

export const INITIAL_ADVANCED_FORM = {
  trailingEnabled: false,
  // Tipo de trailing (WP gestión-trade): 'callback' (retroceso %, el de
  // siempre) | 'atr' (chandelier k×ATR desde el pico). Un solo slot.
  trailingType: 'callback',
  trailingActivation: '', // R de beneficio que arman el trailing
  trailingCallback: '', // % de retroceso del precio desde el máximo que dispara el cierre
  trailingAtrPeriod: '14', // periodo del ATR (solo type 'atr')
  trailingAtrMultiplier: '3', // k del chandelier (solo type 'atr')
  breakevenEnabled: true,
  breakevenActivation: '1', // R de beneficio que mueven el SL a breakeven
  breakevenLock: '0', // % adicional fijado sobre la entrada (0 = SL en entrada)
  breakevenOnTp1: true, // BE al tocar TP1 (independiente del umbral)
  dynamicEnabled: false,
  dynamicStep: '', // R a favor que mueven el SL un paso
  dynamicMaxSteps: 0, // 0 = ilimitado
  // SL al TP conquistado (solo Opc. 1, sl_tp_source='indicator'): al TP_k
  // (k ≥ 2) el SL sube al precio de TP_{k-1}. Sin más parámetros (§2.2).
  conqueredTpEnabled: false,
}

// Equivalente en % del precio de N R con un SL de slPct % (solo informativo,
// para ② con SL fijo). null si no hay datos para calcularlo.
export function rToPercent(r, slPct) {
  const rv = Number(r)
  const sv = Number(slPct)
  if (r === '' || slPct === '' || slPct == null || !(rv > 0) || !(sv > 0)) return null
  return Math.round(rv * sv * 100) / 100
}

// Aplana los configs del detalle del bot (PublicBotResponse — los Decimal
// llegan como strings JSON) a las claves planas del form.
export function flattenAdvancedFromBot(bot) {
  const tr = bot.trailing_config ?? {}
  const be = bot.breakeven_config ?? {}
  const dy = bot.dynamic_sl_config ?? {}
  const cq = bot.conquered_tp_config ?? {}
  return {
    trailingEnabled: !!tr.enabled,
    // type ausente (bots de antes del WP) = callback (compat total, §2.1).
    trailingType: tr.type === 'atr' ? 'atr' : 'callback',
    trailingActivation: tr.enabled ? String(tr.activation_profit ?? '') : '',
    trailingCallback: tr.enabled ? String(tr.callback_rate ?? '') : '',
    trailingAtrPeriod: tr.enabled ? String(tr.atr_period ?? '14') : '14',
    trailingAtrMultiplier: tr.enabled ? String(tr.atr_multiplier ?? '3') : '3',
    breakevenEnabled: !!be.enabled,
    breakevenActivation: be.enabled ? String(be.activation_profit ?? '') : '',
    // El lock aplica a los dos disparadores: se precarga si cualquiera está activo.
    breakevenLock: (be.enabled || be.on_tp1 !== false) ? String(be.lock_profit ?? '0') : '',
    // Clave ausente (bots antiguos) = true: es lo que el engine hacía siempre.
    breakevenOnTp1: be.on_tp1 !== false,
    dynamicEnabled: !!dy.enabled,
    dynamicStep: dy.enabled ? String(dy.step_percent ?? '') : '',
    dynamicMaxSteps: dy.enabled ? Number(dy.max_steps ?? 0) : 0,
    // Conquista: clave ausente o {} = desactivada (sin backfill, §2.2).
    conqueredTpEnabled: cq.enabled === true,
  }
}

// Validación client-side (el backend solo valida lock_profit >= 0 además del
// tipo): cada gestión activada necesita sus parámetros con valores sanos.
// Devuelve el motivo (texto para el usuario) o null si es válida: el panel de
// edición lo muestra junto a "Guardar" -- un bloqueo nunca es mudo
// (incidente ST WLD 1H, 2026-10-16).
//
// WP gestión-trade: las reglas nuevas (slot único de trailing, parámetros del
// ATR y conquista solo con niveles del indicador) llevan los mensajes ESPEJO
// EXACTOS de los 422 del backend (diseño §4.2) — el usuario ve lo mismo antes
// y después del servidor. `indicatorSource` (Opc. 1) lo aporta el caller:
// editBlockReasons lo recibe del panel/wizard.
export function advancedManagementError(form, { indicatorSource = false } = {}) {
  const positive = (v) => v !== '' && Number(v) > 0
  const nonNegative = (v) => v !== '' && Number(v) >= 0
  if (form.trailingEnabled) {
    const trailingType = form.trailingType ?? 'callback'
    if (trailingType !== 'callback' && trailingType !== 'atr') {
      return TRAILING_SLOT_ERROR
    }
    if (trailingType === 'callback') {
      if (!(positive(form.trailingActivation) && positive(form.trailingCallback))) {
        return 'Trailing stop: indica la activación (R) y el retroceso (%), ambos mayores que 0.'
      }
    } else {
      if (!positive(form.trailingActivation)) {
        return 'Trailing por ATR: indica la activación (R) mayor que 0.'
      }
      const period = Number(form.trailingAtrPeriod)
      if (!(Number.isInteger(period) && period >= 2) || !positive(form.trailingAtrMultiplier)) {
        return TRAILING_ATR_PARAMS_ERROR
      }
    }
  }
  if (form.conqueredTpEnabled && !indicatorSource) {
    return CONQUERED_TP_SOURCE_ERROR
  }
  if (form.breakevenEnabled && !positive(form.breakevenActivation)) {
    return 'Breakeven: indica la activación (R de beneficio) mayor que 0.'
  }
  if ((form.breakevenEnabled || form.breakevenOnTp1) && !nonNegative(form.breakevenLock)) {
    return 'Breakeven: el beneficio extra debe ser 0 o mayor.'
  }
  if (
    form.dynamicEnabled &&
    !(positive(form.dynamicStep) && Number.isInteger(Number(form.dynamicMaxSteps)) && Number(form.dynamicMaxSteps) >= 0)
  ) {
    return 'SL dinámico: indica el paso (R) mayor que 0 y un máximo de pasos entero (0 = ilimitado).'
  }
  return null
}

export function validateAdvancedManagement(form, opts) {
  return advancedManagementError(form, opts) === null
}

// Construye los configs DESDE EL FORM (nunca eco del bot): lo que la UI
// no gestiona no se reenvía. Desactivado => { enabled: false } explícito.
//
// WP gestión-trade (diseño §6.1, reglas corregidas):
// - `type` viaja SOLO con trailing enabled (trailing off => payload byte a
//   byte idéntico a master — DIFF_0).
// - El slot es exclusivo ESTRUCTURAL: con type "callback" NUNCA se emiten
//   atr_*; con type "atr" NUNCA callback_rate (si no, la propia web
//   provocaría el 422 de slot del backend, §4.2.1).
// - `conquered_tp_config` se emite SOLO cuando enabled: true (OFF => clave
//   ausente — mismo DIFF_0).
export function buildAdvancedManagementPayload(form) {
  const trailingType = form.trailingType === 'atr' ? 'atr' : 'callback'
  return {
    trailing_config: form.trailingEnabled
      ? trailingType === 'atr'
        ? {
            enabled: true,
            type: 'atr',
            activation_profit: Number(form.trailingActivation),
            atr_period: parseInt(form.trailingAtrPeriod, 10),
            atr_multiplier: Number(form.trailingAtrMultiplier),
          }
        : {
            enabled: true,
            type: 'callback',
            activation_profit: Number(form.trailingActivation),
            callback_rate: Number(form.trailingCallback),
          }
      : { enabled: false },
    // on_tp1 viaja SIEMPRE explícito (independiente del umbral): ausente,
    // el backend lo trataría como true.
    breakeven_config: {
      enabled: !!form.breakevenEnabled,
      ...(form.breakevenEnabled ? { activation_profit: Number(form.breakevenActivation) } : {}),
      ...((form.breakevenEnabled || form.breakevenOnTp1) && form.breakevenLock !== ''
        ? { lock_profit: Number(form.breakevenLock) }
        : {}),
      on_tp1: !!form.breakevenOnTp1,
    },
    dynamic_sl_config: form.dynamicEnabled
      ? {
          enabled: true,
          step_percent: Number(form.dynamicStep),
          max_steps: parseInt(form.dynamicMaxSteps, 10) || 0,
        }
      : { enabled: false },
    ...(form.conqueredTpEnabled ? { conquered_tp_config: { enabled: true } } : {}),
  }
}
